import React from "react";
import { Box, Typography } from "@mui/material";
import type {
  IStreamData,
  IThumbnailTemplate,
} from "../../../../../types/thumbnail-template";
import muzukuruBlack from "../../../../../assets/muzukurublack.png";
import muzukuruWhite from "../../../../../assets/muzukuru.png";
import oldMutuallogo from "../../../../../assets/old-mutual.png";
import firstmutuallogo from "../../../../stream-management/components/thumbnail/assets/firstmutual.png";

// ─── Types ────────────────────────────────────────────────────────────────────

interface StreamCardProps {
  stream: IStreamData;
  thumbRec:
    | {
        thumbnail: IThumbnailTemplate;
        deceased?: {
          id: number;
          first_name: string;
          surname: string;
          second_name: string;
          date_of_birth: string;
          date_of_death: string;
          gender: string;
          person_image: string;
        }[];
      }
    | undefined;
  formatDate: (d: string | undefined, s: string | undefined) => string;
}

// ─── Component ────────────────────────────────────────────────────────────────

const StreamCard: React.FC<StreamCardProps> = ({
  stream,
  thumbRec,
  formatDate,
}) => {
  const template = thumbRec?.thumbnail;

  const categoryRaw = template?.category || "conference";
  const category = categoryRaw.toLowerCase();
  const isFuneral = category.includes("funeral");

  const link = stream.website_stream_link;

  const bgImage =
    template?.background_image ||
    (stream as IStreamData & { custom_background?: string })?.custom_background;
  const venue =
    stream?.venue_name ||
    stream?.location ||
    stream?.service?.venue?.name ||
    "Venue Name";

  const city =
    stream?.address || stream?.service?.venue?.city || "Harare, Zimbabwe";

  const startTime = stream?.shoot_from || stream?.start_time || "";

  // ── Match against the fully-resolved venue string (mirrors FuneralTemplate) ──
  // Previously these only checked stream.location and stream.venue_name as
  // raw fields, which missed cases where the venue resolved from
  // stream.service?.venue?.name. Now we check the already-resolved `venue`.
  const isOldMutual = /old mutual/i.test(venue);
  const isFirstMutual = /first mutual/i.test(venue);

  // ── Shared base styles ─────────────────────────────────────────────────────
  const cardBaseSx = {
    position: "relative",
    borderRadius: "14px 14px 0 0",
    overflow: "hidden",
    cursor: link ? "pointer" : "default",
    display: "flex",
    backgroundImage: bgImage ? `url(${bgImage})` : "none",
    backgroundColor: "#1a0c3a",
    backgroundSize: "cover",
    backgroundPosition: "center",
    color: "#fff",
    fontFamily: '"Montserrat", sans-serif',
    transition: "all 0.2s ease-in-out",
    "&:hover": link
      ? { filter: "brightness(1.1)", transform: "translateY(-2px)" }
      : {},
  };

  // ── FUNERAL LAYOUT ─────────────────────────────────────────────────────────
  if (isFuneral) {
    const deceasedArrayItem = stream.booking?.deceased?.[0];
    const bookingData = stream.bookingData;

    const firstName =
      deceasedArrayItem?.first_name || bookingData?.deceased_name || "";
    const lastName =
      deceasedArrayItem?.surname || bookingData?.deceased_surname || "";
    const secondName =
      deceasedArrayItem?.second_name || bookingData?.deceased_second_name || "";

    const funeralName = firstName
      ? `${firstName} ${secondName} ${lastName}`.replace(/\s+/g, " ").trim()
      : stream.title || "In Loving Memory";

    const photo =
      deceasedArrayItem?.person_image ||
      bookingData?.deceased_photo_url ||
      stream.person_image;

    return (
      <Box
        onClick={() => link && window.open(link, "_blank")}
        sx={{
          ...cardBaseSx,
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          width: "100%",
          aspectRatio: "16 / 9",
          minHeight: "unset",
          padding: "10px 14px",
        }}
      >
        {/* LEFT PANEL */}
        <Box
          sx={{
            flex: 1,
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {photo ? (
            <>
              <Box
                sx={{
                  width: 95,
                  height: 95,
                  borderRadius: "50%",
                  overflow: "hidden",
                  border: "3px solid rgba(255,255,255,0.85)",
                  boxShadow: "0 3px 10px rgba(0,0,0,0.4)",
                  mb: 1,
                }}
              >
                <img
                  src={photo}
                  alt=""
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "85%",
                  mb: 0.75,
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    height: "0.75px",
                    bgcolor: "rgba(255,255,255,0.4)",
                  }}
                />
                <Typography
                  sx={{
                    px: 0.75,
                    fontSize: 6.5,
                    fontWeight: 900,
                    letterSpacing: 1,
                  }}
                >
                  {isOldMutual || isFirstMutual
                    ? "SERVICE PROVIDED BY"
                    : "BROADCAST BY"}
                </Typography>
                <Box
                  sx={{
                    flex: 1,
                    height: "0.75px",
                    bgcolor: "rgba(255,255,255,0.4)",
                  }}
                />
              </Box>

              <Box
                sx={{
                  mt: 0.5,
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <img
                  src={
                    isOldMutual
                      ? oldMutuallogo
                      : isFirstMutual
                        ? firstmutuallogo
                        : muzukuruWhite
                  }
                  alt="Provider Logo"
                  style={{
                    width: "auto",
                    height: "32px",
                    objectFit: "contain",
                    display: "block",
                  }}
                />
              </Box>
            </>
          ) : (
            <>
              <Typography
                sx={{
                  fontSize: 9,
                  fontWeight: 700,
                  fontStyle: "italic",
                  mb: 0.25,
                }}
              >
                In Loving Memory Of
              </Typography>
              <Typography sx={{ fontSize: 16, fontWeight: 800, mb: 0.25 }}>
                {funeralName}
              </Typography>
              <Typography sx={{ fontSize: 7, fontWeight: 700, mb: 0.5 }}>
                {stream.date || ""}
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  mb: 0.25,
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    height: "0.75px",
                    bgcolor: "rgba(255,255,255,0.4)",
                  }}
                />
                <Typography
                  sx={{
                    px: 0.75,
                    fontSize: 6.5,
                    fontWeight: 900,
                    letterSpacing: 1,
                  }}
                >
                  {isOldMutual || isFirstMutual
                    ? "SERVICE PROVIDED BY"
                    : "BROADCAST BY"}
                </Typography>
                <Box
                  sx={{
                    flex: 1,
                    height: "0.75px",
                    bgcolor: "rgba(255,255,255,0.4)",
                  }}
                />
              </Box>

              <Box
                sx={{
                  mt: 0.5,
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <img
                  src={
                    isOldMutual
                      ? oldMutuallogo
                      : isFirstMutual
                        ? firstmutuallogo
                        : muzukuruWhite
                  }
                  alt="Provider Logo"
                  style={{
                    width: "auto",
                    height: "32px",
                    objectFit: "contain",
                    display: "block",
                  }}
                />
              </Box>
            </>
          )}
        </Box>

        {/* RIGHT PANEL */}
        <Box sx={{ flex: 1, textAlign: "center" }}>
          <Typography sx={{ fontSize: 7.5, fontStyle: "italic", opacity: 0.9 }}>
            In Loving Memory of
          </Typography>
          <Typography sx={{ fontSize: 16, fontWeight: 800, mb: 0.25 }}>
            {funeralName}
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 0.5,
              mb: 0.5,
            }}
          >
            <Box sx={{ bgcolor: "red", px: 0.5, py: 0.3, borderRadius: "2px" }}>
              <Typography sx={{ fontSize: 6.5, fontWeight: 900 }}>
                LIVE
              </Typography>
            </Box>
            <Typography
              sx={{ fontSize: 6.5, fontWeight: 800, letterSpacing: 1 }}
            >
              STREAM
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, lineHeight: 1.2 }}>
            {formatDate(stream.date, startTime)}
          </Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 900, mb: 0.5 }}>
            {startTime} HRS (CAT)
          </Typography>
          <Typography
            sx={{
              fontSize: 6,
              fontWeight: 700,
              opacity: 0.8,
              letterSpacing: 1,
            }}
          >
            BROADCASTING LIVE FROM
          </Typography>
          <Typography sx={{ fontSize: 10, fontWeight: 700 }}>
            {venue}
          </Typography>
          <Typography sx={{ fontSize: 8, fontWeight: 600, opacity: 0.9 }}>
            {city}
          </Typography>
        </Box>
      </Box>
    );
  }

  // ── CONFERENCE / CHURCH LAYOUT ─────────────────────────────────────────────
  const eventTitle = stream?.title || "Name of Event";

  return (
    <Box
      onClick={() => link && window.open(link, "_blank")}
      sx={{
        ...cardBaseSx,
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
        width: "100%",
        aspectRatio: "16 / 9",
        minHeight: "unset",
        padding: "10px 14px",
      }}
    >
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 900,
          textTransform: "uppercase",
          letterSpacing: 0.6,
          lineHeight: 1.15,
          mb: 0.5,
          width: "100%",
        }}
      >
        {eventTitle}
      </Typography>

      <Typography sx={{ fontSize: 8, fontWeight: 600, opacity: 0.85, mb: 1 }}>
        {formatDate(stream.date, startTime)}
      </Typography>

      <Box
        sx={{
          bgcolor: "rgba(5, 49, 44, 0.97)",
          borderRadius: "3px 14px 14px 3px",
          px: 1.5,
          py: 0.85,
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          width: "92%",
          mb: 1,
        }}
      >
        <Box sx={{ textAlign: "left", flexShrink: 0 }}>
          <Box
            sx={{ display: "flex", gap: 0.5, alignItems: "center", mb: 0.25 }}
          >
            <Box
              sx={{
                bgcolor: "red",
                color: "#fff",
                fontSize: 6.5,
                fontWeight: 900,
                px: 0.5,
                py: 0.15,
                borderRadius: "1px",
                lineHeight: 1.4,
              }}
            >
              LIVE
            </Box>
            <Typography
              sx={{
                fontSize: 6.5,
                fontWeight: 800,
                color: "#fff",
                letterSpacing: 0.5,
              }}
            >
              STREAM
            </Typography>
          </Box>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 900,
              color: "#fff",
              whiteSpace: "nowrap",
              lineHeight: 1.2,
            }}
          >
            {startTime?.slice(0, 5)} HRS (CAT)
          </Typography>
        </Box>

        <Box
          sx={{
            width: "1px",
            height: 26,
            bgcolor: "rgba(255,255,255,0.25)",
            flexShrink: 0,
          }}
        />

        <Box sx={{ textAlign: "right", flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 6.5,
              fontWeight: 700,
              color: "rgba(255,255,255,0.65)",
              textTransform: "uppercase",
              letterSpacing: 0.5,
              lineHeight: 1.3,
            }}
          >
            Broadcasting Live From
          </Typography>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 800,
              color: "#fff",
              lineHeight: 1.2,
              wordBreak: "break-word",
            }}
          >
            {venue}
          </Typography>
          <Typography
            sx={{
              fontSize: 8,
              fontWeight: 600,
              color: "rgba(255,255,255,0.8)",
              wordBreak: "break-word",
            }}
          >
            {city}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{ display: "flex", alignItems: "center", width: "80%", mb: 0.75 }}
      >
        <Box
          sx={{ flex: 1, height: "0.75px", bgcolor: "rgba(255,255,255,0.25)" }}
        />
        <Typography
          sx={{
            px: 1,
            fontSize: 6.5,
            fontWeight: 900,
            opacity: 0.75,
            letterSpacing: 1,
            whiteSpace: "nowrap",
          }}
        >
          {isOldMutual || isFirstMutual
            ? "SERVICE PROVIDED BY"
            : "BROADCAST BY"}
        </Typography>
        <Box
          sx={{ flex: 1, height: "0.75px", bgcolor: "rgba(255,255,255,0.25)" }}
        />
      </Box>

      <Box sx={{ width: 72, height: "auto" }}>
        <img
          src={
            isOldMutual
              ? oldMutuallogo
              : isFirstMutual
                ? firstmutuallogo
                : muzukuruBlack
          }
          alt={
            isOldMutual
              ? "Old Mutual Logo"
              : isFirstMutual
                ? "First Mutual Logo"
                : "Muzukuru"
          }
          style={{
            width: "100%",
            height: "44px",
            objectFit: "contain",
            display: "block",
          }}
        />
      </Box>
    </Box>
  );
};

export default StreamCard;
